import React from 'react';

import type { Identifier, XYCoord } from 'dnd-core'
import cx from 'classnames';
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { DraggableListDataItem, DraggableListDirection } from '../../draggable-list.types';

import './item.styles.scss';

export type ItemProps = {
  className?: string;
  data: DraggableListDataItem;
  index: number;
  direction: DraggableListDirection;
  onDraggingStatusChange: (isDragging: boolean) => void;
  renderContent: (item: DraggableListDataItem) => React.ReactNode;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
};


type DragItem = DraggableListDataItem & {
  index: number;
  type: string;
};

export const Item: React.FC<ItemProps> = (props) => {
  const { data, index, direction, renderContent, moveCard, className, onDraggingStatusChange } = props;
  const ITEM_TYPE = 'AM_DRAGGABLE_LIST_ITEM_TYPE';

  const content = React.useMemo(() => {
    return renderContent(data);
  }, [renderContent, data]);

  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ITEM_TYPE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      if (direction === DraggableListDirection.vertical) {
        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
         
        // Get pixels to the top
        const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
 
        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%
 
        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
 
        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }
      }

      if (direction === DraggableListDirection.horizontal) {
        const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;        
        const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRect.left;
 
        if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
          return;
        }

        if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
          return;
        }
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: () => {
      return { id: data.id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });


  drag(drop(ref));

  React.useEffect(() => {
    onDraggingStatusChange(isDragging);
  }, [isDragging]);

  return (
    <div
      className={cx('DraggableListItem', { ['DraggableListItem--projection']: isDragging }, className)}
      ref={ref}
      data-handler-id={handlerId}
    >
      {content}
    </div>
  );
};
